import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  LinearProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  School,
  Dashboard,
  Grade,
  Assignment,
  Schedule,
  CheckCircle,
  Event,
  TrendingUp,
  EventAvailable,
  Campaign,
  Warning,
  Info,
  MenuBook,
  Folder
} from '@mui/icons-material';
import ResourcesTab from '../components/ResourcesTab';

const StudentPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('studentUser');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem('studentUser');
    localStorage.removeItem('userRole');
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login');
  };

  const stats = { gpa: '3.7', attendance: '96%', pending: 2, nextTest: 'Jan 22' };

  const mockGrades = [
    { subject: 'Mathematics', teacher: 'Mr. Davis', grade: 'A-', pct: 92, assignment: 'Algebra Quiz', date: '2024-01-15' },
    { subject: 'English Language Arts', teacher: 'Mrs. Thompson', grade: 'B+', pct: 88, assignment: 'Essay on Literature', date: '2024-01-12' },
    { subject: 'Science', teacher: 'Mr. Garcia', grade: 'A', pct: 96, assignment: 'Lab Report', date: '2024-01-10' },
    { subject: 'History', teacher: 'Mrs. Williams', grade: 'B', pct: 84, assignment: 'Civil War Project', date: '2024-01-08' },
    { subject: 'Bible Studies', teacher: 'Pastor Lee', grade: 'A', pct: 98, assignment: 'Scripture Memory', date: '2024-01-07' },
    { subject: 'Physical Education', teacher: 'Coach Adams', grade: 'A+', pct: 100, assignment: 'Fitness Assessment', date: '2024-01-05' },
  ];

  const mockAssignments = [
    { subject: 'Mathematics', assignment: 'Chapter 5 Homework', dueDate: '2024-01-22', status: 'Pending' },
    { subject: 'English Language Arts', assignment: 'Book Report — The Giver', dueDate: '2024-01-25', status: 'In Progress' },
    { subject: 'Science', assignment: 'Chemistry Lab Write-up', dueDate: '2024-01-18', status: 'Completed' },
    { subject: 'History', assignment: 'WWII Timeline', dueDate: '2024-01-30', status: 'Pending' },
    { subject: 'Bible Studies', assignment: 'Psalm 23 Memorization', dueDate: '2024-01-19', status: 'Completed' },
  ];

  const weeklySchedule = [
    { period: '1st — 8:00 AM', monday: 'Mathematics', tuesday: 'Science', wednesday: 'Mathematics', thursday: 'Science', friday: 'Mathematics' },
    { period: '2nd — 9:30 AM', monday: 'English', tuesday: 'Mathematics', wednesday: 'History', thursday: 'Mathematics', friday: 'English' },
    { period: '3rd — 11:00 AM', monday: 'Science', tuesday: 'English', wednesday: 'English', thursday: 'English', friday: 'Bible Studies' },
    { period: '4th — 1:00 PM', monday: 'History', tuesday: 'Bible Studies', wednesday: 'Science Lab', thursday: 'History', friday: 'History' },
    { period: '5th — 2:30 PM', monday: 'Phys. Ed.', tuesday: 'Music', wednesday: 'Art', thursday: 'Phys. Ed.', friday: 'Chapel' },
  ];

  const attendanceRecords = [
    { date: 'Jan 15, 2024', status: 'Present' },
    { date: 'Jan 12, 2024', status: 'Present' },
    { date: 'Jan 11, 2024', status: 'Tardy' },
    { date: 'Jan 10, 2024', status: 'Present' },
    { date: 'Jan 09, 2024', status: 'Absent' },
    { date: 'Jan 08, 2024', status: 'Present' },
    { date: 'Jan 05, 2024', status: 'Present' },
  ];

  const announcements = [
    { title: 'Parent-Teacher Conferences', body: 'Scheduled for January 25–26. Your parents will receive sign-up links via email.', severity: 'info' },
    { title: 'Science Fair Registration Open', body: 'Register your project by February 1. See Mr. Garcia for entry forms.', severity: 'success' },
    { title: 'Chapel Service — Friday', body: 'Mandatory chapel service this Friday at 2:30 PM in the main auditorium.', severity: 'warning' },
  ];

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'success';
    if (grade.startsWith('B')) return 'warning';
    return 'error';
  };

  const getStatusColor = (status) => {
    if (status === 'Completed' || status === 'Present') return 'success';
    if (status === 'In Progress' || status === 'Tardy') return 'warning';
    return 'error';
  };

  const todaySchedule = weeklySchedule.map(p => ({ period: p.period, class: p.monday }));

  if (!isAuthenticated) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <School sx={{ fontSize: 60, color: 'primary.main', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>Student Portal</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Sign in to access your grades, assignments, schedule, and more.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/login')} sx={{ px: 4, py: 1.5 }}>
              Sign In
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '1.4rem' }}>
                {user?.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Welcome, {user?.name}!</Typography>
                {(user?.grade || user?.id) && (
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {user?.grade}{user?.grade && user?.id ? ' • ' : ''}{user?.id ? `ID: ${user.id}` : ''}
                  </Typography>
                )}
              </Box>
            </Box>
            <Button variant="outlined" onClick={handleSignOut} sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Sign Out
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Stat Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: 'Current GPA', value: stats.gpa, icon: <TrendingUp />, color: 'success.main' },
            { label: 'Attendance Rate', value: stats.attendance, icon: <EventAvailable />, color: 'primary.main' },
            { label: 'Pending Assignments', value: stats.pending, icon: <Assignment />, color: 'warning.main' },
            { label: 'Next Test', value: stats.nextTest, icon: <Grade />, color: 'error.main' },
          ].map((stat, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <Box sx={{ color: stat.color, mb: 0.5 }}>{stat.icon}</Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color }}>{stat.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<Dashboard />} label="Dashboard" iconPosition="start" />
            <Tab icon={<Grade />} label="Grades" iconPosition="start" />
            <Tab icon={<Assignment />} label="Assignments" iconPosition="start" />
            <Tab icon={<Schedule />} label="Schedule" iconPosition="start" />
            <Tab icon={<EventAvailable />} label="Attendance" iconPosition="start" />
            <Tab icon={<Folder />} label="Resources" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Dashboard Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Announcements */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Campaign color="primary" /> School Announcements
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {announcements.map((ann, i) => (
                  <Alert key={i} severity={ann.severity} sx={{ borderRadius: 2 }}>
                    <AlertTitle sx={{ fontWeight: 600 }}>{ann.title}</AlertTitle>
                    {ann.body}
                  </Alert>
                ))}
              </Box>
            </Grid>

            {/* Today's Schedule */}
            <Grid item xs={12} md={5}>
              <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule color="primary" /> Today's Schedule
                  </Typography>
                  <List dense disablePadding>
                    {todaySchedule.map((item, i) => (
                      <ListItem key={i} sx={{ px: 0, borderBottom: i < todaySchedule.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <ListItemText
                          primary={item.class}
                          secondary={item.period}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Upcoming Events */}
            <Grid item xs={12} md={7}>
              <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event color="primary" /> Upcoming Events
                  </Typography>
                  <List dense disablePadding>
                    {[
                      { event: 'Psalm 23 Memorization Due', date: 'Jan 19', type: 'assignment' },
                      { event: 'Chemistry Lab Write-up Due', date: 'Jan 22', type: 'assignment' },
                      { event: 'Parent-Teacher Conference', date: 'Jan 25–26', type: 'school' },
                      { event: 'Science Fair Registration Deadline', date: 'Feb 1', type: 'school' },
                      { event: 'Winter Break', date: 'Feb 15–19', type: 'holiday' },
                    ].map((item, i) => (
                      <ListItem key={i} sx={{ px: 0, borderBottom: i < 4 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {item.type === 'assignment' ? <Assignment sx={{ fontSize: 16, color: 'warning.main' }} /> :
                           item.type === 'school' ? <Event sx={{ fontSize: 16, color: 'primary.main' }} /> :
                           <Schedule sx={{ fontSize: 16, color: 'success.main' }} />}
                        </ListItemIcon>
                        <ListItemText primary={item.event} secondary={item.date} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Grades Tab */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Current Grades</Typography>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Latest Assignment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockGrades.map((g, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{g.subject}</TableCell>
                      <TableCell>{g.teacher}</TableCell>
                      <TableCell>{g.assignment}</TableCell>
                      <TableCell>
                        <Chip label={g.grade} color={getGradeColor(g.grade)} size="small" sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={g.pct} sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} color={getGradeColor(g.grade)} />
                          <Typography variant="caption">{g.pct}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{g.date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Assignments Tab */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Current Assignments</Typography>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assignment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockAssignments.map((a, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{a.subject}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{a.assignment}</TableCell>
                      <TableCell>{a.dueDate}</TableCell>
                      <TableCell>
                        <Chip label={a.status} color={getStatusColor(a.status)} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Schedule Tab */}
        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Weekly Class Schedule</Typography>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Monday</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tuesday</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Wednesday</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Thursday</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Friday</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklySchedule.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>{row.period}</TableCell>
                      <TableCell>{row.monday}</TableCell>
                      <TableCell>{row.tuesday}</TableCell>
                      <TableCell>{row.wednesday}</TableCell>
                      <TableCell>{row.thursday}</TableCell>
                      <TableCell>{row.friday}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Attendance Tab */}
        {activeTab === 4 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Attendance Summary</Typography>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Days Present', value: 142, color: 'success' },
                        { label: 'Days Absent', value: 4, color: 'error' },
                        { label: 'Tardies', value: 2, color: 'warning' },
                        { label: 'Attendance Rate', value: '96%', color: 'info' },
                      ].map((s, i) => (
                        <Grid item xs={6} key={i}>
                          <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: `${s.color}.light`, borderRadius: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: `${s.color}.dark` }}>{s.value}</Typography>
                            <Typography variant="caption" color={`${s.color}.dark`}>{s.label}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Records</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attendanceRecords.map((r, i) => (
                            <TableRow key={i}>
                              <TableCell>{r.date}</TableCell>
                              <TableCell>
                                <Chip label={r.status} color={getStatusColor(r.status)} size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
        {/* Resources Tab */}
        {activeTab === 5 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>School Resources</Typography>
            <ResourcesTab role="student" />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default StudentPortal;
